import { Link } from "react-router-dom";

import LegalLayout from "./LegalLayout";
import { config } from "../../config";

const LAST_UPDATED = "June 21, 2026";

export default function TermsPage() {
  const brand = config.brandName;
  const parent = config.parentName || "JoatLabs.dev";
  const contact = config.contactEmail || "hello@orbitcell.io";

  return (
    <LegalLayout title="Terms of Service" lastUpdated={LAST_UPDATED}>
      <p>
        These terms govern your use of the {brand} marketing site and waitlist.
        They are written in plain English and intended to be read.
      </p>

      <h2>What this is</h2>
      <p>
        Today, this public site is the {brand} marketing site, waitlist, and
        product sign-in handoff. By joining the waitlist you are asking us to
        email you when access opens or expands. Joining the waitlist does not
        create product access, sign you up for a paid service, or commit us to
        a launch date or feature set. Product application access is handled
        separately from the public waitlist flow.
      </p>

      <h2>Who you are agreeing with</h2>
      <p>
        {brand} is operated by <strong>{parent}</strong>. References to
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; and &ldquo;{brand}&rdquo; mean
        {" "}{parent}.
      </p>

      <h2>Acceptance</h2>
      <p>
        By submitting the waitlist form, you agree to these terms. If you do
        not agree, please do not submit the form.
      </p>

      <h2>Eligibility</h2>
      <p>
        You may join the waitlist if you are at least 16 years old and have
        the authority to provide your work contact information.
      </p>

      <h2>What you provide</h2>
      <p>
        You agree to provide accurate information on the waitlist form. Joining
        the waitlist with a fake company, a colleague&rsquo;s email without
        their permission, or a misrepresented role is not allowed and may
        result in removal from the list.
      </p>

      <h2>What we send you</h2>
      <ul>
        <li>A one-time confirmation email when you join the waitlist</li>
        <li>
          Occasional product updates leading up to launch (typically once a
          month or less)
        </li>
        <li>An invitation to early access when it opens</li>
        <li>Pricing information once tiers are finalized</li>
      </ul>
      <p>
        Every email contains an unsubscribe link. You can also email{" "}
        <a href={`mailto:${contact}`}>{contact}</a> to be removed at any time.
      </p>

      <h2>What we don&rsquo;t promise</h2>
      <p>
        This is a pre-launch communication channel, not a product. We do not
        promise:
      </p>
      <ul>
        <li>A specific launch date</li>
        <li>A specific set of features</li>
        <li>
          A specific price &mdash; waitlist members will get founder pricing
          for 12 months after launch, but pricing terms are subject to change
          before launch
        </li>
        <li>Continuous availability of the marketing site</li>
      </ul>

      <h2>Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          Submit content that is unlawful, infringing, defamatory, harassing,
          or fraudulent
        </li>
        <li>
          Attempt to circumvent the form&rsquo;s rate limits or bot defenses
        </li>
        <li>
          Reverse-engineer, scrape, or attempt unauthorized access to the site
          or our infrastructure
        </li>
        <li>Use the site to send unsolicited communications to others</li>
      </ul>

      <h2>Intellectual property</h2>
      <p>
        The {brand} name, logo, marketing copy, and site design are owned by
        {" "}{parent}. The information you submit on the waitlist form remains
        yours; you grant us a limited license to use it for the purposes
        described in our <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <h2>No warranty</h2>
      <p>
        The marketing site and the waitlist are provided <strong>&ldquo;as
        is&rdquo; and &ldquo;as available.&rdquo;</strong> We do not warrant
        that the site will be uninterrupted, error-free, or fit for a
        particular purpose. Use of the site is at your own risk.
      </p>

      <h2>Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, {parent} and its officers,
        employees, and contractors are not liable for any indirect,
        incidental, special, consequential, or punitive damages arising from
        your use of the marketing site or the waitlist. Our total liability
        for direct damages will not exceed one hundred US dollars ($100).
      </p>
      <p>
        These limits do not apply where they cannot lawfully be limited (for
        example, certain consumer protections in your jurisdiction).
      </p>

      <h2>Termination</h2>
      <p>
        You can leave the waitlist at any time by clicking the unsubscribe
        link or emailing <a href={`mailto:${contact}`}>{contact}</a>. We may
        remove you from the waitlist or restrict your access to the site at
        any time, for any reason, including suspected abuse or violation of
        these terms.
      </p>

      <h2>Privacy</h2>
      <p>
        Your use of the site is also governed by our{" "}
        <Link to="/privacy">Privacy Policy</Link>. By joining the waitlist you
        acknowledge you have read it.
      </p>

      <h2>Changes</h2>
      <p>
        We may update these terms when the public site, waitlist, or product
        access changes. The &ldquo;last updated&rdquo; date reflects the most
        recent change.
      </p>

      <h2>Contact</h2>
      <p>
        Questions: <a href={`mailto:${contact}`}>{contact}</a>.
      </p>
    </LegalLayout>
  );
}
